import React, { useState, useEffect } from 'react'
import loadable from '@loadable/component'
import PropTypes from 'prop-types'
import makeStyles from '@mui/styles/makeStyles'
import { createTheme } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import LsFeeIcon from '../../assets/image/svg/LsFeeIcon.inline.svg'
import PaxosIcon from '../../assets/image/svg/e_ibkrpaxos.inline.svg'
import CryptoComIcon from '../../assets/image/svg/cryptoComLogo.inline.svg'
import GemIcon from '../../assets/image/svg/e_gemini.inline.svg'
import CoinIcon from '../../assets/image/svg/e_coinbase.inline.svg'
import WithTheme from '../../helpers/WithTheme'
import {Text} from '../Core'
import {breakpoints} from '../../utils/breakpoints'
import { useWindowResize } from '../Header/HeaderHooks'
import { styled } from '@mui/material/styles';


const Paper = loadable(() => import('@mui/material/Paper'))
const Box = loadable(() => import('@mui/material/Box'))
const Grid = loadable(() => import('@mui/material/Grid'))
const Table = loadable(() => import('@mui/material/Table'))
const TableCell = loadable(() => import('@mui/material/TableCell'))
const TableRow = loadable(() => import('@mui/material/TableRow'))
const TableBody = loadable(() => import('@mui/material/TableBody'))
const TableHead = loadable(() => import('@mui/material/TableHead'))
const TableContainer = loadable(() => import('@mui/material/TableContainer'))
const FeesCalculator = loadable(() => import('../FeesCalculator'))


// Overrides for material-ui table

// const {t} = useTranslation();

// function createData(id, txFee, spread, totalCost ) {
//   return { id, txFee, spread, totalCost };
// }

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1
  }
  if (b[orderBy] > a[orderBy]) {
    return 1
  }
  return 0
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy)
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index])
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0])
    if (order !== 0) return order
    return a[1] - b[1]
  })
  return stabilizedThis.map(el => el[0])
}

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
  },
  paper: {
    width: '100%',
    marginBottom: theme.spacing(2),
    borderRadius: 0,
    overflow: 'hidden',
    backgroundColor: 'transparent',
    boxShadow: 'none'
  },
  table: {
    minWidth: 600,
  },
  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    top: 20,
    width: 1,
  },
}))

const Item = styled(Paper)(({ theme, userDarkMode }) => ({
  backgroundColor: 'transparent',
  // textAlign: 'center',
  alignContent: 'center',
  justifyContent: 'center',
  marginTop: 5,
  borderBottom: userDarkMode ? '1px solid rgba(255,255,255,0.15)' : '1px solid rgba(0,0,0,0.15)',
  height: 70,
  // width: 60,
  boxShadow: 'none',
  borderRadius: 0,
  paddingTop: 8,
  paddingRight: 4,
  fontWeight: 900,
  color: userDarkMode ? 'rgba(255, 255, 255, 0.56)' : 'rgba(0, 0, 0, 0.56)'
}));

const ExchFeesTable = props => {
  const { t, ready } = useTranslation(['tables'], { useSuspense: false })
  const userSettings = useSelector(state => state.user.user_settings)
  const userSessionExpired = useSelector(state => state.user.session_expired)
  const userDarkModeSelector = useSelector(state => state.user.darkMode)
  const userDarkMode = userSessionExpired
    ? userDarkModeSelector
    : typeof userSettings !== 'undefined'
    ? userSettings.dark_mode
    : false
  
  const feesTotalCost = useSelector(state => state.exchData.totalCost)
  const feesOrderSide = useSelector(state => state.exchData.orderSide)

  const [windowWidth, resizeWidth] = useWindowResize()

  useEffect(() => {
    resizeWidth()
  }, [])


  const classes = useStyles()
  const [order] = useState('asc')
  const [orderBy] = useState('calories')
  const [page] = useState(0)
  const [rowsPerPage] = useState(13)

  const headCells = [
    {
      id: 'id',
      numeric: true,
      disablePadding: false,
      label: '',
    },
    {
      id: 'txFee',
      numeric: false,
      disablePadding: false,
      label: ready && (feesOrderSide === 1 ? `${t('addedFees')}` : `${t('costToTrade')}`),
    },
    // {
    //   id: 'spread',
    //   numeric: false,
    //   disablePadding: false,
    //   label: `${t('spread')}`,
    // },
    {
      id: 'totalCost',
      numeric: true,
      disablePadding: false,
      label: ready && (feesOrderSide === 1 ? `${t('totalTxCost')}` : `${t('totalProceeds')}`),
    },
  ]

  function EnhancedTableHead() {
    return ready && (
      <TableHead>
        <TableRow>
          {headCells.map((headCell, index) => (
            <TableCell
              style={{ color: userDarkMode ? 'rgba(255, 255, 255, 0.56)' : 'rgba(0, 0, 0, 0.56)', textAlign: 'right' }}
              key={index}
              align="right"
            >
              {headCell.label}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
    )
  }

  EnhancedTableHead.propTypes = {
    classes: PropTypes.object.isRequired,
    // onRequestSort: PropTypes.func.isRequired,
    // order: PropTypes.oneOf(['asc', 'desc']).isRequired,
    // orderBy: PropTypes.string.isRequired,
  }

  let rows = [
    {
      id: 'Lightspeed Crypto',
      icon: <LsFeeIcon className="mr-0 mr-md-2" aria-hidden="true" />,
      txFee: 0,
      spread: '0.15%',
      totalCost: parseFloat(feesTotalCost).toFixed(2).toLocaleString(undefined),
    },
    {
      id: 'IBKR Paxos',
      icon: <PaxosIcon className="mr-2" aria-hidden="true" />,
      txFee: parseFloat(feesTotalCost * 0.0018).toFixed(2),
      spread: ready &&  `${t('none')}`,
      totalCost: feesOrderSide === 1 ? 
        parseFloat(feesTotalCost + (feesTotalCost * 0.0018)).toFixed(2).toLocaleString(undefined) :
        parseFloat(feesTotalCost - (feesTotalCost * 0.0018)).toFixed(2).toLocaleString(undefined)
      ,
    },
    {
      id: 'Gemini ActiveTrader',
      icon: <GemIcon className="mr-2" aria-hidden="true" />,
      txFee: parseFloat(feesTotalCost * 0.0035).toFixed(2),
      spread:ready && `${t('none')}`,
      totalCost: feesOrderSide === 1 ?  
        parseFloat(feesTotalCost + (feesTotalCost * 0.0035)).toFixed(2).toLocaleString(undefined) :
        parseFloat(feesTotalCost - (feesTotalCost * 0.0035)).toFixed(2).toLocaleString(undefined)
      ,
    },
    {
      id: 'Crypto.com',
      icon: <CryptoComIcon className="mr-2" aria-hidden="true" />,
      txFee: parseFloat(feesTotalCost * 0.004).toFixed(2),
      spread: ready && `${t('none')}`,
      totalCost: feesOrderSide === 1 ? 
        parseFloat(feesTotalCost + (feesTotalCost * 0.004)).toFixed(2).toLocaleString(undefined) :
        parseFloat(feesTotalCost - (feesTotalCost * 0.004)).toFixed(2).toLocaleString(undefined)   
      ,
    },
    {
      id: 'Coinbase Pro',
      icon: <CoinIcon className="mr-2" aria-hidden="true" />,
      txFee: parseFloat(feesTotalCost * 0.006).toFixed(2),
      spread: ready && `${t('none')}`,
      totalCost: feesOrderSide === 1 ? 
        parseFloat(feesTotalCost + (feesTotalCost * 0.006)).toFixed(2).toLocaleString(undefined) :
        parseFloat(feesTotalCost - (feesTotalCost * 0.006)).toFixed(2).toLocaleString(undefined)
      ,
    },
    
  ]

  const emptyRows =
    rowsPerPage - Math.min(rowsPerPage, rows.length - page * rowsPerPage)

  if (windowWidth < breakpoints.md) {
    return ready && (
      <div 
        style={{
          border: userDarkMode ? '1px solid rgba(255,255,255,0.15)' : '1px solid rgba(0,0,0,0.15)',
          borderRadius: 10,
          backgroundColor: userDarkMode ? '#0C0E11' : '#F0F0F0',
          marginBottom: 40,
        }} 
        className={classes.root}
      >
        <FeesCalculator/>
        <Box overflow="scroll" sx={{ flexGrow: 1 }}>
          <Grid container columns={6} spacing={-8} alignItems="center">
            <Grid item xs>
              <Item></Item>
              {
                stableSort(rows, getComparator(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, index) => {
                  return (
                  <Item style={{textAlign: 'left'}} key={index} className="pl-3">{row.icon}</Item>
                  )
                })
              }
            </Grid>
            <Grid item xs>
              <Item></Item>
              {
                stableSort(rows, getComparator(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, index) => {
                  return (
                  <Item style={{textAlign: 'left', fontSize: 16, fontWeight: 400, color: userDarkMode ? 'rgba(255, 255, 255, 0.56)' : 'rgba(0, 0, 0, 0.56)' }} key={index} >{row.id}</Item>
                  )
                })
              }
            </Grid>
            <Grid className="mr-3 ml-1" item xs={1}>
              <Item style={{color: userDarkMode ? 'rgba(255, 255, 255, 0.56)' : 'rgba(0, 0, 0, 0.56)' , textAlign: 'right', fontSize: 14, fontWeight: 400}}>{t('addedFees')}</Item>
              {
                stableSort(rows, getComparator(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, index) => {
                  return (
                  <Item key={index} style={{color: userDarkMode ? 'rgba(255, 255, 255, 0.56)' : 'rgba(0, 0, 0, 0.56)' , textAlign: 'right', fontSize: 16, fontWeight: 700, fontVariant: ['tabular-nums'], color: row.txFee === 0 ? '#599273' : '#FB4C42'}}>
                    ${
                      row.txFee === 0 ?
                      row.txFee.toFixed(2) :
                      row.txFee
                    }
                  </Item>
                  )
                })
              }
            </Grid>
            <Grid className="ml-4"  item xs={1}>
              <Item className="pr-3" style={{color: userDarkMode ? 'rgba(255, 255, 255, 0.56)' : 'rgba(0, 0, 0, 0.56)' , textAlign: 'right', fontSize: 14, fontWeight: 400,}}>{feesOrderSide === 1 ? t('totalTxCost') : t('totalProceeds')}</Item>
              {
                stableSort(rows, getComparator(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, index) => {
                  return (
                  <Item className="pr-3" style={{color: userDarkMode ? 'rgba(255, 255, 255, 0.56)' : 'rgba(0, 0, 0, 0.56)' , textAlign: 'right', fontSize: 16, fontWeight: 400, fontVariant: ['tabular-nums']}} key={index}>${row.totalCost !== 'NaN' ? row.totalCost : 0}</Item>
                  )
                })
              }
            </Grid>
          </Grid>
        </Box>
        <Text className="m-3" fontSize={14} color={userDarkMode ? 'rgba(255, 255, 255, 0.56)' : 'rgba(0, 0, 0, 0.56)'} variant="small">{t('disclosure')}</Text>
      </div>
    )
  }

  return ready && (
    <div 
      style={{
        border: userDarkMode ? '1px solid rgba(255,255,255,0.15)' : '1px solid rgba(0,0,0,0.15)',
        borderRadius: 10,
        backgroundColor: userDarkMode ? '#0C0E11' : '#F0F0F0',
        marginBottom: 20
      }} 
      className={classes.root}
    >
      <FeesCalculator />
      <Paper style={{width: '100%'}} className={classes.paper}>
        <TableContainer>
          <Table
            className={classes.table}
            aria-label="Pricing table"         
          >
            <EnhancedTableHead classes={classes} />
            <TableBody>
            
              {stableSort(rows, getComparator(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, index) => {
                  return (
                    <TableRow hover tabIndex={-1} key={index}>
                      <TableCell
                        style={{ color: userDarkMode ? '#FAFAFA' : 'black' }}
                        align="left"
                      >
                        {row.icon} {row.id}
                      </TableCell>
                      {
                        row.txFee === 0 ?
                        <TableCell
                        style={{ color: '#599273', fontWeight: 700 }}
                        align="right"
                      >
                        ${row.txFee.toFixed(2)}
                      </TableCell>
                      :
                      <TableCell
                        style={{ color: '#FB4C42', fontWeight: 700 }}
                        align="right"
                      >
                        ${row.txFee}
                      </TableCell>
                      }
                      
                      {/* <TableCell
                        style={{ color: userDarkMode ? '#FAFAFA' : 'black' }}
                        align="right"
                      >
                        {row.spread}
                      </TableCell> */}
                      <TableCell
                          style={{ color: userDarkMode ? 'rgba(255, 255, 255, 0.56)' : 'rgba(0, 0, 0, 0.56)' }}
                          align="right"
                        >
                          ${row.totalCost !== 'NaN' ? row.totalCost : 0}{' '}
                        </TableCell>
                    </TableRow>
                  )
                })}
              {emptyRows > 0 && (
                <TableRow>
                </TableRow>
              )}
              
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      <Text className="mx-3 mb-3" fontSize={13} color={userDarkMode ? 'rgba(255, 255, 255, 0.56)' : 'rgba(0, 0, 0, 0.56)'} variant="small">{t('disclosure')}</Text>
    </div>
  )
}

const muiTheme = ({ userDarkMode }) =>
  createTheme({
    components: {
      MuiTableBody: {
        styleOverrides: {
          root: {
            responsive: 'simple'
          }
        }
      },
      MuiTableCell: {
        styleOverrides: {
          body: {
            fontSize: '16px',
            fontFamily: 'CircularStd, sans-serif !important',
            fontWeight: 300,
            padding: '10px 16px',
            fontVariant: ['tabular-nums'],
          },
          head: {
            fontFamily: 'CircularStd, sans-serif !important',
            fontStyle: 'normal',
            fontWeight: 300,
            fontSize: '13px',
            lineHeight: '20px',
            textAlign: 'center',
            padding: '10px 16px',
          },
          root: { borderBottom: userDarkMode ? '1px solid rgba(255,255,255,0.15)' : '1px solid rgba(0,0,0,0.15)'},
        },
      },
    },
    mixins: {},
    palette: {
      text: { hint: 'rgba(0, 0, 0, 0.38)' },
      mode: 'light',
      type: 'light',
    },
  })

export default WithTheme(muiTheme)(ExchFeesTable)
